// src/AddressAutocomplete.tsx

import React, { useEffect, useRef } from "react";
import { TextField } from "@mui/material";
import useOnclickOutside from "react-cool-onclickoutside";
import usePlacesAutocomplete from "use-places-autocomplete";

export type AddressAutocompleteProps = {
  value: string;
  onChange: (val: string) => void;
  /** Called when the user picks a place: formatted address + coords */
  onSelect?: (address: string, lat: number, lng: number) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  error?: boolean;
};

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  disabled = false,
  placeholder = "Start typing an address…",
  className,
  error,
}) => {
  const {
    ready,
    setValue: setAutoValue,
    clearSuggestions,
  } = usePlacesAutocomplete({ debounce: 300 });

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useOnclickOutside(() => clearSuggestions());

  useEffect(() => {
    // Only initialize once the Maps JS is loaded
    if (
      !window.google ||
      !window.google.maps ||
      !window.google.maps.places ||
      !inputRef.current
    )
      return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        componentRestrictions: { country: ["us"] },
        fields: ["formatted_address", "geometry"],
        types: ["address"],
      }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const formatted = place.formatted_address || "";
      // update hook’s internal value (no new fetch)
      setAutoValue(formatted, false);
      // notify parent of the new address
      onChange(formatted);

      // extract coords if available
      const loc = place.geometry?.location;
      if (loc && onSelect) {
        const lat = loc.lat();
        const lng = loc.lng();
        onSelect(formatted, lat, lng);
      }
    });
  }, [onChange, onSelect, setAutoValue]);

  return (
    <div ref={containerRef}>
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={(e) => {
          const newVal = e.target.value;
          setAutoValue(newVal);
          onChange(newVal);
        }}
        label="Address"
        fullWidth
        margin="normal"
        placeholder={placeholder}
        disabled={disabled || !ready}
        className={className}
        error={error}
      />
    </div>
  );
};

export default AddressAutocomplete;
